/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Inject, Injectable} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {MEDIA_TYPE, MediaTypeHelper} from '../../components/util/media-type-helper';
import {simplifyFee} from '../line-item/line-item-fee-utils';
import {formatIsoCurrency} from '../../components/util/iso-currency-format';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {LayoutApiService} from '../admin/layout/layout-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';

export const DEFAULT_LAYOUT_TYPES = ['IMAGE', 'MIXED', 'TEXT', 'HTML', 'VIDEO', 'AUDIO', 'THIRD_PARTY', 'FLASH', 'RETAIL'];

export function resolveDefaultCreativeName(nameTemplate: string, lineItemName: string, fileName?: string): string {
  const template = nameTemplate === '{fileName}' && !fileName ? 'Creative for {lineItemName}' : nameTemplate;
  return (template || '').replace('{fileName}', fileName || '').replace('{lineItemName}', lineItemName || '');
}

@Injectable({
  providedIn: 'root'
})
export class CreativeHelper {
  constructor(
    @Inject(LayoutApiService) private readonly Layout: LayoutApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly uiRouter: UIRouter,
    private readonly appLocale: AppLocaleService,
  ) {
  }

  isolateAssetComponents(layouts: any[]): void {
    _.forEach(layouts, (layout) => {
      layout.assetComponents = _.filter(layout.layoutComponents, (lc) => {
        return lc.type === 'ASSET';
      });
    });
  }

  deriveFee(lineItemPromise: any, ctrl: any, fee: any): any {
    return Promise.resolve(lineItemPromise).then(() => {
      const grossCpm = _.cloneDeep(_.get(ctrl, ['lineItem', 'bidSpecification', 'cpm']));
      const grossCpc = _.cloneDeep(_.get(ctrl, ['lineItem', 'bidSpecification', 'cpc']));
      const referenceCurrency = _.get(grossCpm, ['currency'])
        || _.get(grossCpc, ['currency'])
        || _.get(ctrl, ['lineItem', 'billingCurrency'])
        || 'NA';
      return simplifyFee(this.LocalNetworkProfile, referenceCurrency, fee);
    });
  }

  getLayouts(ctrl: any, defaultLayoutType: string, lineItemPromise: any): any {
    const editingModel = ctrl.editingModel || ctrl.model || {};
    let layoutContext = {};
    if (ctrl.isCreative) {
      if (editingModel.id) {
        layoutContext = {creativeId: editingModel.id};
      } else if (_.get(editingModel, ['lineItem', 'id']) || typeof ctrl.lineItemParent === 'string') {
        layoutContext = {lineItemId: _.get(editingModel, ['lineItem', 'id']) || ctrl.lineItemParent};
      } else if (_.get(editingModel, ['creativeSet', 'id']) || typeof ctrl.creativeSetParent === 'string') {
        layoutContext = {creativeSetId: _.get(editingModel, ['creativeSet', 'id']) || ctrl.creativeSetParent};
      }
    }
    return firstValueFrom(this.Layout.listOptions(layoutContext)).then((page: any) => {
      const layouts = page.results;

      ctrl.layoutRef = {};
      const feePromises = _.map(layouts, (l) => {
        const indexValue = DEFAULT_LAYOUT_TYPES.indexOf(l.detectedCategory || l.type);

        const feePromise = Promise.resolve(this.deriveFee(lineItemPromise, ctrl, _.get(l, ['advertiserFee', 'fee'])).then((fee: any) => {
          if (fee) {
            l.name = l.name + ' - ' + formatIsoCurrency(fee.monetary.amount, fee.monetary.currency, undefined, undefined, undefined, this.LocalNetworkProfile.get('defaultCurrency'), this.appLocale.getLocaleId()) + ' fee';
          }
        }));
        l.ogLayoutType = l.layoutType;
        if (l.layoutCategory) {
          l.layoutType = l.layoutCategory;
          l.layoutTypeOrderBy = 0;
        } else if (indexValue > -1) {
          l.layoutType = DEFAULT_LAYOUT_TYPES[indexValue];
          l.layoutTypeOrderBy = indexValue + 1;
        } else {
          l.layoutType = defaultLayoutType;
          l.layoutTypeOrderBy = DEFAULT_LAYOUT_TYPES.length + 1;
        }
        ctrl.layoutRef[l.id] = l;
        return feePromise;
      });

      return Promise.all(feePromises).then(() => {
        ctrl.layouts = _.orderBy(layouts, ['layoutTypeOrderBy', 'name'], ['asc', 'asc']);
        this.isolateAssetComponents(ctrl.layouts);
      });
    });
  }

  checkHtmlUrls(ctrl: any): void {
    if (ctrl.primaryHtmlAsset) {
      ctrl.primaryHtmlAsset = {
        ...ctrl.primaryHtmlAsset,
        hasUrls: !_.isEmpty(ctrl.primaryHtmlAsset.htmlUrls)
      };
    }
  }

  doLayoutParams(ctrl: any, withModel?: any): void {
    const theModel = withModel || ctrl.editingModel || ctrl.model;

    const allLayoutParameters = _.assign({}, theModel.cToText, theModel.cToUrls, theModel.cToChoices);
    _.forEach(theModel.cToAssets, (cToA, key) => {
      if (!cToA || !cToA.id) {
        return;
      }
      allLayoutParameters[key] = {
        id: cToA.id
      };
    });
    theModel.layoutParameters = _.cloneDeep(allLayoutParameters);
    _.forEach(allLayoutParameters, (lpValue, lpKey) => {
      if (!_.find(ctrl.allComponents, ['tag', lpKey])) {
        delete theModel.layoutParameters[lpKey];
      }
    });
    delete theModel.constraintsToAssets;
    delete theModel.constraintsToText;
    delete theModel.constraintsToUrls;
    delete theModel.constraintsToChoices;
  }

  updateDefaultName(ctrl: any, justGoDefaultName?: boolean, assignedAsset?: any): void {
    const model = ctrl.editingModel || ctrl.model;
    const networkDefaults = this.LocalNetworkProfile.getDefaults();
    const defCreativeName = networkDefaults.defaultCreativeName;
    const lineItemName = this.uiRouter.globals.params.modelName || this.uiRouter.globals.params.lineItemName || _.get(ctrl, ['lineItemInfo', 'name'], '');
    const savedPlaceholderName = resolveDefaultCreativeName(defCreativeName, lineItemName);
    const canReplaceSavedPlaceholder = defCreativeName === '{fileName}'
      && (model.name === savedPlaceholderName || model.name === 'Creative for {lineItemName}');
    if (!ctrl.isNew && !canReplaceSavedPlaceholder) {
      return;
    }
    const noFileNameReplace = defCreativeName.indexOf('{fileName}') < 0;
    if (noFileNameReplace && !justGoDefaultName) {
      return;
    }
    const asset = assignedAsset || _.find(model.cToAssets, (a) => {
      return a;
    });
    if (!asset || !asset.fileName) {
      return;
    }
    const assetName = asset.fileName;
    model.name = justGoDefaultName && noFileNameReplace ? assetName || 'Creative' : resolveDefaultCreativeName(defCreativeName, lineItemName, assetName);
  }

  updateCreativeDims(ctrl: any, prevAssetAssignments: any, forceGetFromAsset?: boolean): void {
    const model = ctrl.editingModel || ctrl.model;
    const noText = _.filter(ctrl.allComponents, (c) => {
      return c.type === 'TEXT' || c.type === 'CHOICE';
    }).length === 0;
    const images = _.filter(ctrl.allComponents, (c) => {
      return c.type === 'ASSET' && !MediaTypeHelper.isMediaType(c.mimeTypes, MEDIA_TYPE.html);
    });
    const htmls = _.filter(ctrl.allComponents, (c) => {
      return c.type === 'ASSET' && MediaTypeHelper.isMediaType(c.mimeTypes, MEDIA_TYPE.html);
    });
    if (forceGetFromAsset || noText) {
      if ((images.length === 1 && htmls.length === 0) || (htmls.length === 1 && images.length === 0)) {
        const currentTag = images.length === 1 ? images[0].tag : htmls[0].tag;
        const selAsset = model.cToAssets[currentTag];
        if (selAsset && (forceGetFromAsset || prevAssetAssignments[currentTag])) {
          if (forceGetFromAsset || !_.isFinite(model.width) || !_.isFinite(model.height) || (prevAssetAssignments[currentTag].width === model.width && prevAssetAssignments[currentTag].height === model.height)) {
            model.width = selAsset.width;
            model.height = selAsset.height;
          }
        }
        prevAssetAssignments[currentTag] = selAsset;
      }
    }
  }

  updateCreativeDimsFromVideoUpload(ctrl: any, uploadResponse: any): boolean {
    const model = ctrl.editingModel || ctrl.model;
    const uploadedAssets = _.flatten(_.map(_.castArray(uploadResponse || []), (asset) => {
      return asset && asset.results ? asset.results : asset;
    }));
    const uploadedVideo = _.find(uploadedAssets, (asset) => {
      const mediaType = MediaTypeHelper.getMediaType(_.get(asset, 'mimeType'));
      return _.get(mediaType, 'video') === true && _.get(asset, 'width') > 0 && _.get(asset, 'height') > 0;
    });
    if (!uploadedVideo) {
      return false;
    }
    model.width = uploadedVideo.width;
    model.height = uploadedVideo.height;
    return true;
  }

  matchConstraintsAndAssets(ctrl: any, mInitPromises: any[], mLayoutAssetMatches: any, prevAssetAssignments: any, delayedPreview: () => void, newAssetUpload?: boolean): any {
    const model = ctrl.editingModel || ctrl.model;
    const loadConstraintsAndAssignDefaultAssets = () => {
      const layout = model.layout;
      ctrl.isFullPage = !!layout?.fullPage;
      if (!layout || !layout.id) {
        // in this case, the layout has become inactive or deleted
        ctrl.allResolved = true;
        return;
      }

      ctrl.allComponents = layout.layoutComponents;

      const acsForLayout = _.filter(ctrl.allComponents, (c) => {
        return c.type === 'ASSET';
      });
      if (!mLayoutAssetMatches[layout.id]) {
        mLayoutAssetMatches[layout.id] = acsForLayout;
      }

      model.cToAssets = model.cToAssets || {};
      model.cToText = model.cToText || {};
      model.cToUrls = model.cToUrls || {};
      model.cToChoices = model.cToChoices || {};
      _.forEach(model.layoutParameters, (lpValue, lpKey) => {
        const component = _.find(ctrl.allComponents, ['tag', lpKey]) || {};
        if (component.type === 'ASSET') {
          model.cToAssets[lpKey] = model.cToAssets[lpKey] || lpValue;
        } else if (component.type === 'URL') {
          model.cToUrls[lpKey] = model.cToUrls[lpKey] || lpValue;
        } else if (component.type === 'TEXT') {
          model.cToText[lpKey] = model.cToText[lpKey] || lpValue;
        } else if (component.type === 'CHOICE') {
          model.cToChoices[lpKey] = model.cToChoices[lpKey] || lpValue;
        }
      });

      _.forEach(ctrl.allComponents, (component) => {
        if (component.type === 'TEXT') {
          component.pattern = '';
          component.patternData = '';
          if (component.minLength >= 0 && component.maxLength >= 0) {
            component.pattern = '(.|\n|\t){' + component.minLength + ',' + component.maxLength + '}';
            component.patternData = {minLength: component.minLength, maxLength: component.maxLength};
          }

          if (!_.has(model.cToText, component.tag) && component.defaultValue) {
            model.cToText[component.tag] = component.defaultValue;
          }
        }
        if (component.type === 'CHOICE') {
          if (!model.cToChoices[component.tag] && component.defaultValue && component.options.indexOf(component.defaultValue) > -1) {
            model.cToChoices[component.tag] = component.defaultValue;
          }
        }
      });

      _.forEach(model.cToAssets, (asset) => {
        const availableAsset = _.find(model.assets, (a) => {
          return a && asset && a.id === asset.id;
        });
        const availableLibraryAsset = _.find(_.get(model, 'libraryCreativeAssets', []), (a) => {
          return a && asset && a.id === asset.id;
        });
        if (_.isString(asset) || _.isFinite(asset)) {
          // this can happen on layout transition
          asset = '';
        } else if (!availableLibraryAsset && asset && (!availableAsset || availableAsset.objectState === 'HIDDEN')) {
          asset.deleted = true;
        } else if (availableLibraryAsset) {
          asset.fromLibrary = true;
        }
      });

      ctrl.acsWithAssetMatches = mLayoutAssetMatches[layout.id];
      if (!ctrl.acsWithAssetMatches) {
        return;
      }

      ctrl.acsWithAssetMatches = _.keyBy(ctrl.acsWithAssetMatches, 'tag');
      // automatically assigns assets to layout's asset constraints.
      const usedAssetIds = {};
      let doPreview = false;
      let newlyAssignedAsset: any;
      _.forEach(ctrl.acsWithAssetMatches, (acWithAssetMatches) => {
        if (acWithAssetMatches.required === 'MANDATORY' && acWithAssetMatches.tag && (!model.cToAssets[acWithAssetMatches.tag] || _.get(model.cToAssets[acWithAssetMatches.tag], 'deleted') === true) && acWithAssetMatches.matchingAssets && acWithAssetMatches.matchingAssets.length > 0) {
          let asset = _.find(acWithAssetMatches.matchingAssets, (matchingAsset) => {
            return !usedAssetIds[matchingAsset.id];
          });
          asset = asset || acWithAssetMatches.matchingAssets[0];
          model.cToAssets[acWithAssetMatches.tag] = asset;
          usedAssetIds[asset.id] = asset.id;
          newlyAssignedAsset = asset;
          doPreview = true;
        }
      });

      // do the html selection, which assumes one html mime type asset constraint available
      _.forEach(ctrl.acsWithAssetMatches, (acWithAssetMatches) => {
        if (MediaTypeHelper.isMediaType(acWithAssetMatches.mimeTypes, MEDIA_TYPE.html)) {
          // get the full asset information
          const htmlAsset = model.cToAssets[acWithAssetMatches.tag];
          if (htmlAsset && htmlAsset.id && htmlAsset.id !== _.get(ctrl.primaryHtmlAsset, 'id')) {
            ctrl.primaryHtmlAsset = _.find(acWithAssetMatches.matchingAssets, (a) => {
              return a.id === htmlAsset.id;
            });
            this.checkHtmlUrls(ctrl);
          }
        }
      });

      _.forEach(model.cToAssets, (val, tag) => {
        prevAssetAssignments[tag] = val;
        if (!prevAssetAssignments[tag] || !prevAssetAssignments[tag].width) {
          prevAssetAssignments[tag] = _.find(model.assets, (asset) => {
            return asset && val && asset.id && val.id && asset.id === val.id;
          }) || val;
        }
      });

      if (newAssetUpload && newlyAssignedAsset) {
        this.updateDefaultName(ctrl, false, newlyAssignedAsset);
      }
      if (_.isFunction(ctrl.eventHook.onAssetUpdate)) {
        ctrl.eventHook.onAssetUpdate(!newAssetUpload);
      }

      if (doPreview) {
        if (newAssetUpload) {
          this.updateCreativeDims(ctrl, prevAssetAssignments, true);
        }
        delayedPreview();
      }
      ctrl.allResolved = true;
    };

    return Promise.all(mInitPromises).then(() => {
      const layout = model.layout;
      const creative = model;
      if (!layout) {
        ctrl.isFullPage = false;
        ctrl.allResolved = true;
        return;
      }
      if (!creative) {
        return loadConstraintsAndAssignDefaultAssets();
      }
      const acsWithAssets = [];
      const layoutAcs = layout.assetComponents;
      ctrl.primaryHtmlAsset = undefined;
      _.forEach(layoutAcs, (ac) => {
        // can only be one html asset constraint per layout
        ac.matchingAssets = _.filter(_.get(creative, ['assets'], []).concat(_.get(model, 'libraryCreativeAssets', [])), (asset) => {
          const assetMediaType = MediaTypeHelper.isMediaType(ac.mimeTypes, MEDIA_TYPE.html);
          const fileCheck = asset.objectState !== 'HIDDEN' && _.includes(ac.mimeTypes, asset.mimeType) &&
            (ac.maxFileSizeBytes < 1 || asset.fileSizeBytes <= ac.maxFileSizeBytes) &&
            (!asset.htmlCreativeType || assetMediaType) &&
            (!assetMediaType || asset.primaryHtmlAsset);
          if (asset.primaryHtmlAsset && assetMediaType) {
            ctrl.primaryHtmlAsset = asset;
          }
          return fileCheck;
        });
        acsWithAssets.push(ac);
      });
      mLayoutAssetMatches[layout.id] = acsWithAssets;
      this.checkHtmlUrls(ctrl);

      _.forEach(creative.assets, (asset) => {
        asset.matchingAcs = asset.matchingAcs || {};
        asset.matchingAcs[layout.id] = _.map(_.filter(acsWithAssets, (ac) => {
          return _.find(ac.matchingAssets, ['id', asset.id]);
        }), 'tag');
      });
      loadConstraintsAndAssignDefaultAssets();
      ctrl.allowPreviews = true;
    });
  }
}
